"use client";

import React, {
  forwardRef,
  RefCallback,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useCombobox } from "downshift";
import { AutocompleteGameCard } from "modules/cards/model/card";
import { Search, X } from "lucide-react";
import { TailwindTooltipCardImage } from "../TailwindTooltipCardImage";
import { AutocompleteListItem } from "./AutocompleteListItem";
import { cn } from "utils/utils";
import debounce from "lodash/debounce";
import { Input } from "../ui/Input";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

interface TailwindCardAutocompleteProps {
  onInputChange: (value: string) => void;
  onClear: () => void;
  options: AutocompleteGameCard[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  loadMoreRef:
    | RefCallback<HTMLDivElement>
    | React.RefObject<HTMLDivElement | null>;
  error?: Error | null;
  renderItem?: (item: AutocompleteGameCard, props: any) => React.ReactNode;
  onSelect?: (item: AutocompleteGameCard) => void;
  tooltipPlacement?: "top" | "right" | "bottom" | "left";
  shouldClearInput?: boolean;
  className?: string;
  inputClassName?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  keyboardShortcut?: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

export interface TailwindCardAutocompleteHandle {
  reset: () => void;
}

export const TailwindCardAutocomplete = forwardRef<
  TailwindCardAutocompleteHandle,
  TailwindCardAutocompleteProps
>(function TailwindCardAutocomplete(
  {
    onInputChange,
    onClear,
    options,
    isLoading,
    isFetchingNextPage,
    loadMoreRef,
    error,
    renderItem,
    onSelect,
    tooltipPlacement = "right",
    shouldClearInput = true,
    className,
    inputClassName,
    inputRef,
    placeholder = "Search for a card",
    keyboardShortcut,
    scrollContainerRef,
  }: TailwindCardAutocompleteProps,
  ref
) {
  const [hasInput, setHasInput] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AutocompleteGameCard | null>(
    null
  );

  const debouncedOnInputChange = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length >= 2) {
          onInputChange(value);
        }
      }, 150), // Reduced from 300ms to 150ms for better responsiveness
    [onInputChange]
  );

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    reset,
  } = useCombobox({
    items: options,
    selectedItem,
    onInputValueChange: ({ inputValue }) => {
      const value = inputValue || "";
      setHasInput(value.length > 0);
      debouncedOnInputChange(value);
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      if (newSelectedItem) {
        setSelectedItem(newSelectedItem);
        onSelect?.(newSelectedItem);
        if (shouldClearInput) {
          // Clear immediately instead of using setTimeout to prevent focus issues
          handleClearInput();
        }
      }
    },
    itemToString: (item) => (item ? item.name : ""),
  });
  const handleClearInput = useCallback(() => {
    setHasInput(false);
    setSelectedItem(null);
    onClear();
    reset();
  }, [onClear, reset]);

  useEffect(() => () => debouncedOnInputChange.cancel(), [debouncedOnInputChange]);
  const isLoadingAny = isLoading || isFetchingNextPage;

  // Expose reset method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      reset: handleClearInput,
    }),
    [handleClearInput]
  );

  return (
    <div className={cn("relative w-full", className)}>
      <div className="relative">
        <Input
          {...getInputProps({
            ref: inputRef,
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: false,
          })}
          type="text"
          placeholder={placeholder}
          layout="horizontal"
          startIcon={<Search size={ICON_SIZE} />}
          endContent={
            hasInput ? (
              <button type="button" onClick={handleClearInput}>
                <X
                  size={ICON_SIZE}
                  className="text-gray-400"
                  aria-hidden="true"
                />
              </button>
            ) : null
          }
          isLoading={isLoadingAny}
          className={inputClassName}
        />
        {keyboardShortcut && (
          <div
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
            aria-label="Press / to focus card search"
          >
            {keyboardShortcut}
          </div>
        )}
      </div>
      <ul
        {...getMenuProps({
          ref: scrollContainerRef as
            | React.RefObject<HTMLUListElement | null>
            | undefined,
        })}
        className={cn(
          "z-100 rounded-box bg-base-100 focus:outline-hidden absolute mt-1 max-h-[300px] w-full overflow-y-auto overflow-x-hidden shadow-lg",
          !isOpen && "invisible h-0"
        )}
      >
        {isOpen && (
          <>
            {error && (
              <li className="text-error px-3 py-2 text-center">
                Error: {error.message}
              </li>
            )}
            {!error &&
              options.length > 0 &&
              options.map(
                (item, index) =>
                  item && (
                    <TailwindTooltipCardImage
                      key={item?.id + index || index}
                      card={item}
                      placement={tooltipPlacement}
                      forceOpen={highlightedIndex === index}
                    >
                      {renderItem ? (
                        renderItem(item, {
                          ...getItemProps({ item, index }),
                          highlighted: highlightedIndex === index,
                          className: cn(
                            highlightedIndex === index
                              ? "bg-primary text-primary-content"
                              : index % 2 === 0
                                ? "bg-base-200"
                                : "bg-base-100"
                          ),
                        })
                      ) : (
                        <AutocompleteListItem
                          highlighted={highlightedIndex === index}
                          option={item}
                          itemProps={{
                            ...getItemProps({ item, index }),
                            className: cn(
                              highlightedIndex === index
                                ? "bg-primary text-primary-content"
                                : index % 2 === 0
                                  ? "bg-base-200"
                                  : "bg-base-100"
                            ),
                          }}
                        />
                      )}
                    </TailwindTooltipCardImage>
                  )
              )}
            {!error && !isLoadingAny && hasInput && options.length === 0 && (
              <li className="text-base-content px-3 py-2 text-center">
                No results found
              </li>
            )}
            {isLoadingAny && (
              <li className="text-base-content flex items-center justify-center px-3 py-2">
                <span className="loading loading-sm loading-spinner mr-2"></span>
                Loading...
              </li>
            )}
            <div ref={loadMoreRef} />
          </>
        )}
      </ul>
    </div>
  );
});
